import React, {
    useContext,
    useState,
    useCallback,
    useMemo
} from 'react';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { Link } from 'gw-components-platform-react';
import { Button, CurrencyValue, ModalNextProvider } from '@jutro/components';
import SectionsModalCtrl from 'sg-sections-modal';

import CommonConstants from 'sg-common-constants';
import { useHistory } from 'react-router-dom';
import messages from './SgRenewalUpgradeAccordion.messages';
import SgRenewalSphUpgradeTable from './SgRenewalSphUpgradeTable';
import SgRenewalSpmUpgradeTable from './SgRenewalSpmUpgradeTable';
import SgRenewalSptUpgradeTable from './SgRenewalSptUpgradeTable';

import termsAndConditionsSpm from './data/termsAndConditionsSPM.json';
import termsAndConditionsSph from './data/termsAndConditionsSPH.json';
import termsAndConditionsMessages from './data/termsAndConditions.messages';

function SgRenewalUpgradeAccordion(props) {
    const {
        id,
        renewal,
        isUpgrading,
        backToOriginalOffer,
        showDivider
    } = props;

    const history = useHistory();
    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';
    const homePhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_MOVE_HOUSE_PHONE_NUMBER;
    const motorPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_IPID_OTHER_PHONE_NUMBER;
    const isPhoneMobile = breakpoint === 'phone';

    const isSPH = renewal.productCode === CommonConstants.HOME_PRODUCT_CODE;
    const isSPM = renewal.productCode === (CommonConstants.MOTOR_PRODUCT_CODE || CommonConstants.MOTORHOME_PRODUCT_CODE);
    const isSPT = renewal.productCode === CommonConstants.TRAVEL_PRODUCT_CODE;

    const [accordionOpened, setAccordionOpened] = useState(undefined);
    const showUpgradingContent = useMemo(() => {
        return isUpgrading || false;
    }, [isUpgrading]);

    const shouldShowTCWording = useMemo(() => {
        return !isSPT;
    }, [isSPT]);

    const navigateTo = useCallback((path, state) => {
        history.push(path, state);
    }, [history]);

    const handleAccordionOpenClose = (event) => {
        if (event) {
            event.preventDefault();
        }
        setAccordionOpened(!accordionOpened);
        // todo scroll util
    };

    const messageParams = useMemo(() => {
        return {
            homePhoneNumber: isMobile
                ? <a href={`tel:${homePhoneNumber.replaceAll(' ', '')}`}>{homePhoneNumber}</a>
                : homePhoneNumber,
            motorPhoneNumber: isMobile
                ? <a href={`tel:${motorPhoneNumber.replaceAll(' ', '')}`}>{motorPhoneNumber}</a>
                : <b>{motorPhoneNumber}</b>
        };
    }, [isMobile, homePhoneNumber, motorPhoneNumber]);

    const showTermsAndConditions = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        ModalNextProvider.showModal(
            <SectionsModalCtrl
                sections={isSPM ? termsAndConditionsSpm.data : termsAndConditionsSph.data}
                messages={termsAndConditionsMessages}
                title={translator(messages.FeatureThreeYearFixed)}
                commonSection={termsAndConditionsMessages.CommonSection}
                titleVisibility
                className="sgInfoModal"
                messageParams={messageParams}
            />
        );
    }, [ModalNextProvider]);

    const upgradeHeaderPolicySummary = () => {
        return (
            <div>
                <h3>{translator(messages[`${renewal.productCode}UpgradeToFixedPrice`])}</h3>
                <span>
                    <span>{translator(messages[`${renewal.productCode}FixedPriceInfo1`])}</span>
                    { shouldShowTCWording
                        && (
                            <Link
                                href="/"
                                id={`showTermsAndConditionsFolded_${id}`}
                                onClick={showTermsAndConditions}
                            >
                                {translator(messages.TCApply)}
                            </Link>
                        )
                    }
                    <span>{translator(messages[`${renewal.productCode}FixedPriceInfo2`])}</span>
                </span>
            </div>
        );
    };

    const upgradeHeaderRenewalInfo = () => {
        return (
            <div>
                <h2>{translator(messages.UpgradeHeading)}</h2>
                <div>
                    <p className="sgIntroText">
                        <span>{translator(messages.Pay)}</span>
                        <span id="amountAfterUpgrade">
                            <CurrencyValue
                                currency="gbp"
                                currencyDisplay="symbol"
                                id="CurrencyValue"
                                placeholder="-"
                                showGrouping
                                showFractions
                                tag="span"
                                value={renewal.premium.amount}
                            />
                        </span>
                        <span>{translator(messages.NextThreeYears)}</span>
                    </p>
                </div>
                <div>
                    <p className="sgIntroText">{translator(messages.ExtraFeatures)}</p>
                    <ul className="sgTickedList">
                        <li>{translator(messages.FeatureThreeYearFixed)}</li>
                        <li>{translator(messages[`FeatureOne${renewal.productCode}`])}</li>
                        { isSPM
                        && <li>{translator(messages.FeatureKeyProtection)}</li>
                        }
                        <li>{translator(messages[`FeatureTwo${renewal.productCode}`])}</li>
                        <li>{translator(messages[`FeatureThree${renewal.productCode}`])}</li>
                        { isSPM
                        && <li>{translator(messages.FeatureClaimsPromise)}</li>
                        }
                    </ul>
                </div>
            </div>
        );
    };

    const readMoreLink = () => {
        return (
            <Link href="/" id={`readMoreLink_${id}`} className="sgReadMoreLink" onClick={handleAccordionOpenClose}>
                {translator(messages.ReadMore)}
            </Link>
        );
    };

    const backToOriginalButton = () => {
        return (
            <Button
                id="backToFirstOffeBtn"
                onClick={backToOriginalOffer}
            >
                {translator(messages.BackToOriginalRenewal)}
            </Button>
        );
    };

    const checkMyPriceButton = () => {
        const goToRenewal = () => {
            navigateTo('/renewal',
                {
                    policyNumber: renewal.jobNumber,
                    isUpgrading: true
                });
        };
        return (
            <Button
                type="button"
                id={`upgradeRenewal_${id}`}
                className="sgButton sgSecondaryButton"
                onClick={goToRenewal}
            >
                {translator(messages.CheckMyPrice)}
            </Button>
        );
    };

    const upgradeHeaderCallToActions = () => {
        return (
            <>
                { backToOriginalButton() }
                <Button
                    id="buttonForAccordion{{jobNumber}}"
                    onClick={handleAccordionOpenClose}
                >
                    {translator(messages.CompareButtonLabel)}
                </Button>
            </>
        );
    };

    const upgradeTable = () => {
        return (
            <>
                { isSPM
                && (
                    <SgRenewalSpmUpgradeTable
                        renewal={renewal}
                        id={id}
                        showTermsAndConditions={showTermsAndConditions}
                    />
                )
                }
                { isSPH
                && (
                    <SgRenewalSphUpgradeTable
                        renewal={renewal}
                        id={id}
                        showTermsAndConditions={showTermsAndConditions}
                    />
                )
                }
                { isSPT
                    && (
                        <SgRenewalSptUpgradeTable />
                    )
                }
            </>
        );
    };

    return (
        <div id={`accordion_${id}`} className="sg-renewal-proposal-upgrade-accordion">
            <div className="sg-clear-both" />
            { showDivider
                && (
                    <div className="sg-renewal-proposal-upgrade-separator-message">
                        <hr />
                        <span>{translator(messages.UpgradeDividerMessage)}</span>
                    </div>
                )
            }
            <div className="sg-accordion-head">
                <div className={`${showUpgradingContent ? 'sg-mta-policy-tile-details-container' : 'sg-policy-tile-details-container'}`}>
                    { !showUpgradingContent ? upgradeHeaderPolicySummary() : upgradeHeaderRenewalInfo() }
                    {
                        showUpgradingContent && (
                            <div className="sgBottomButtonsContainer">
                                { upgradeHeaderCallToActions() }
                            </div>
                        )
                    }
                    {!accordionOpened && !showUpgradingContent && isPhoneMobile
                        && (
                            <div className="sg-policy-tile-button-container">
                                { readMoreLink() }
                            </div>
                        )
                    }
                </div>
                {!accordionOpened && !showUpgradingContent && !isPhoneMobile
                    && (
                        <div className="sg-policy-tile-button-container">
                            { readMoreLink() }
                        </div>
                    )
                }
            </div>
            { accordionOpened
                && (
                    <div className="sg-accordion-body">
                        { upgradeTable() }
                        {shouldShowTCWording
                            && (
                                <>
                                    <span>{translator(messages.ViewOurTerms)}</span>
                                    <Link
                                        href="/"
                                        id={`showTermsAndConditionsExpanded_${id}`}
                                        onClick={showTermsAndConditions}
                                    >
                                        {translator(messages.FixedPriceCover)}
                                        .
                                    </Link>
                                </>
                            )
                        }

                        <div className="sgAccordionFooterButtons">
                            <Link id={`hideAccordionBodyBtn_${id}`} className="sg-hide-accordion-link" href="/" onClick={handleAccordionOpenClose}>
                                {translator(messages.ReadLess)}
                            </Link>
                            { !showUpgradingContent ? checkMyPriceButton() : backToOriginalButton() }
                        </div>
                    </div>
                )
            }
        </div>
    );
}

SgRenewalUpgradeAccordion.defaultProps = {
    showDivider: true
};

export default SgRenewalUpgradeAccordion;
